import { action } from '@ember/object';
import { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Task } from 'ember-concurrency';
import { task } from 'ember-concurrency';
import type PrinterAlertConfigurationModel from 'garaje/models/printer-alert-configuration';
import type UserModel from 'garaje/models/user';
import type CurrentLocationService from 'garaje/services/current-location';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type StateService from 'garaje/services/state';
import { SLACK_V2_PLUGIN_KEY, MSTEAMS_V2_PLUGIN_KEY } from 'garaje/utils/enums';
import type { RecordArray } from 'garaje/utils/type-utils';

interface PrinterNotificationsComponentSignature {
  Args: {
    /**
     * set to true if there are no printer at the location
     */
    hasPrinters: boolean;
    searchUsersTask: Task<void, [string, UserModel[] | undefined]>;
  };
}

const PRINTER_ERROR_LABEL_BY_TYPE: Record<number, string> = {
  0: 'Paper feed issue',
  1: 'Incorrect paper size',
  2: 'Out of paper',
  3: 'Cassette missing',
  4: 'Cover open',
  5: 'Paper jam',
};
const NOTIFICATION_CHANNEL_LABEL_BY_TYPE: Record<number, string> = {
  0: 'Email',
  1: 'Slack',
  2: 'Microsoft Teams',
  3: 'SMS',
};

const PRINTER_ERROR_TYPE_BY_LABEL = Object.entries(PRINTER_ERROR_LABEL_BY_TYPE).reduce<Record<string, number>>(
  (carry, [key, label]) => {
    carry[label] = Number(key);
    return carry;
  },
  {},
);

export default class PrinterNotificationsComponent extends Component<PrinterNotificationsComponentSignature> {
  @service declare currentLocation: CurrentLocationService;
  @service declare flashMessages: FlashMessagesService;
  @service declare state: StateService;
  @service declare store: StoreService;

  @tracked installedPluginKeys: Set<string> = new Set();
  @tracked isOpen = false;
  @tracked isEnabled = false;
  @tracked newPrinterContacts: UserModel[] = [];
  @tracked selectedPrinterErrors: string[] = [];
  @tracked printerAlertConfiguration?: PrinterAlertConfigurationModel;
  @tracked selectedNotificationChannels: number[] = [0];
  private initialRecipientIds: number[] = [];
  private initialErrorTypes: number[] = [];
  private initialNotificationChannels: number[] = [];

  printerErrors = Object.keys(PRINTER_ERROR_LABEL_BY_TYPE)
    .map((key) => Number(key))
    .sort((a, b) => a - b)
    .map((key) => PRINTER_ERROR_LABEL_BY_TYPE[key])
    .filter((error): error is string => Boolean(error));

  get hasSlackAvailable(): boolean {
    return this.installedPluginKeys.has(SLACK_V2_PLUGIN_KEY);
  }

  get hasMSTeamsAvailable(): boolean {
    return this.installedPluginKeys.has(MSTEAMS_V2_PLUGIN_KEY);
  }

  get hasSmsAvailable(): boolean {
    return Boolean(this.state.vrSubscription?.canAccessSmsNotification);
  }

  get notificationChannels(): { id: number; label: string }[] {
    return Object.keys(NOTIFICATION_CHANNEL_LABEL_BY_TYPE)
      .map((key) => Number(key))
      .sort((a, b) => a - b)
      .filter((id) => {
        if (id === 0) return true;
        if (id === 1) return this.hasSlackAvailable;
        if (id === 2) return this.hasMSTeamsAvailable;
        if (id === 3) return this.hasSmsAvailable;
        return false;
      })
      .map((key) => ({ id: key, label: NOTIFICATION_CHANNEL_LABEL_BY_TYPE[key]! }));
  }

  get availablePrinterErrors(): string[] {
    const selected = new Set(this.selectedPrinterErrors);
    return this.printerErrors.filter((error) => !selected.has(error));
  }

  constructor(owner: unknown, args: PrinterNotificationsComponentSignature['Args']) {
    super(owner, args);

    void this.fetchPrinterErrorNotificationConfigsTask.perform();
    void this.fetchNotificationChannelPluginsTask.perform();
  }

  get hasChanges(): boolean {
    return Boolean(this.printerAlertConfiguration?.hasDirtyAttributes);
  }

  searchTask = task(async (term: string) => {
    return await this.args.searchUsersTask.perform(term, this.newPrinterContacts);
  });

  fetchNotificationChannelPluginsTask = task(async () => {
    const plugins = await this.store.query('plugin', {
      filter: { notification_channels: true },
      page: { limit: 100 },
    });
    this.installedPluginKeys = new Set(plugins.map((plugin) => plugin.key));
  });

  fetchPrinterErrorNotificationConfigsTask = task(async () => {
    const locationId = this.currentLocation.location.id;
    const configs = (await this.store.query('printer-alert-configuration', {
      filter: { location: locationId },
    })) as RecordArray<PrinterAlertConfigurationModel>;

    const config = configs.slice()[0];
    if (!config) {
      this.printerAlertConfiguration = undefined;
      this.isEnabled = false;
      this.newPrinterContacts = [];
      this.selectedPrinterErrors = [];
      this.selectedNotificationChannels = [0];
      this.initialRecipientIds = [];
      this.initialErrorTypes = [];
      this.initialNotificationChannels = [];
      return;
    }

    this.isEnabled = Boolean(config.enabled);

    const selectedErrors = (config.errorTypes ?? [])
      .map((type: number) => PRINTER_ERROR_LABEL_BY_TYPE[type])
      .filter((error): error is string => Boolean(error));

    let printerContacts: UserModel[] = [];
    if (config.recipientIds?.length) {
      const userPromises = config.recipientIds.map((id) => this.store.findRecord('user', id));
      const users = await Promise.all(userPromises);
      printerContacts = users;
    }

    this.initializeState(config, printerContacts, selectedErrors);
  });

  savePrinterErrorNotificationConfigsTask = task(async () => {
    const config = this.printerAlertConfiguration;
    if (!config || !config.hasDirtyAttributes) {
      return;
    }
    await config.save();
    this.syncInitialValues(config);
    this.flashMessages.showAndHideFlash('success', 'Settings updated');
  });

  @action
  enableNotifications(): void {
    this.isOpen = true;
    this.isEnabled = true;
    const config = this.ensureConfig();
    config.enabled = true;
    void this.savePrinterErrorNotificationConfigsTask.perform();
  }

  @action
  disableNotifications(): void {
    this.isOpen = false;
    this.isEnabled = false;
    const config = this.ensureConfig();
    config.enabled = false;
    void this.savePrinterErrorNotificationConfigsTask.perform();
  }

  @action
  addPrinterContacts(users: UserModel[]): void {
    this.newPrinterContacts = users;
    const config = this.ensureConfig();
    const recipientIds = this.normalizeRecipientIds(users);
    if (this.arraysEqual(recipientIds, this.normalizeNumberArray(this.initialRecipientIds))) {
      config.recipientIds = this.initialRecipientIds;
      return;
    }
    config.recipientIds = recipientIds.map((id) => Number(id));
  }

  @action
  onPrinterErrorSelect(errors: string[]): void {
    this.selectedPrinterErrors = errors;

    const config = this.ensureConfig();
    const errorTypes = this.normalizeErrorTypes(errors);
    if (this.arraysEqual(errorTypes, this.normalizeNumberArray(this.initialErrorTypes))) {
      config.errorTypes = this.initialErrorTypes;
      return;
    }
    config.errorTypes = errorTypes.map((type) => Number(type));
  }

  @action
  toggleNotificationChannel(channelId: number): void {
    const current = this.selectedNotificationChannels;
    const updated = current.includes(channelId) ? current.filter((id) => id !== channelId) : [...current, channelId];

    this.selectedNotificationChannels = updated;

    const config = this.ensureConfig();
    const sorted = updated.slice().sort((a, b) => a - b);
    const initial = this.initialNotificationChannels.slice().sort((a, b) => a - b);
    const isUnchanged = sorted.length === initial.length && sorted.every((v, i) => v === initial[i]);
    config.notificationChannels = isUnchanged ? this.initialNotificationChannels : sorted;
  }

  private initializeState(
    config: PrinterAlertConfigurationModel,
    printerContacts: UserModel[],
    selectedErrors: string[],
  ): void {
    this.printerAlertConfiguration = config;
    this.newPrinterContacts = printerContacts.slice();
    this.selectedPrinterErrors = selectedErrors.slice();
    this.selectedNotificationChannels = (config.notificationChannels ?? [0]).slice();
    this.syncInitialValues(config);
  }

  private syncInitialValues(config: PrinterAlertConfigurationModel): void {
    this.initialRecipientIds = config.recipientIds ?? [];
    this.initialErrorTypes = config.errorTypes ?? [];
    this.initialNotificationChannels = config.notificationChannels ?? [0];
  }

  private ensureConfig(): PrinterAlertConfigurationModel {
    if (this.printerAlertConfiguration) {
      return this.printerAlertConfiguration;
    }

    const config = this.store.createRecord('printer-alert-configuration', {
      location: this.currentLocation.location,
      enabled: this.isEnabled,
      errorTypes: [],
      recipientIds: [],
      notificationChannels: [0],
    });
    this.printerAlertConfiguration = config;
    this.syncInitialValues(config);
    return config;
  }

  private normalizeErrorTypes(errors: string[]): string[] {
    return errors
      .map((error) => String(PRINTER_ERROR_TYPE_BY_LABEL[error]))
      .filter((type) => type !== 'undefined')
      .sort();
  }

  private normalizeRecipientIds(users: UserModel[]): string[] {
    return users
      .map((user) => String(user.id))
      .filter((id) => id !== 'undefined')
      .sort();
  }

  private normalizeNumberArray(values: Array<number | string>): string[] {
    return values.map((value) => String(value)).sort();
  }

  private arraysEqual(left: string[], right: string[]): boolean {
    if (left.length !== right.length) {
      return false;
    }
    return left.every((value, index) => value === right[index]);
  }
}
